import React, { useEffect, useRef, useState } from "react";

import {
  CButton,
  CButtonGroup,
  CCol,
  CFormGroup,
  CInput,
  CLabel,
  CRow,
} from "@coreui/react";

import Loading from "../indicators/Loading";
import { Spinner } from "react-bootstrap";
import Errors, { getFieldErrors } from "../../models/errors";
import { FieldErrors } from "../form/FieldErrors";
import { SUCCESS } from "../../utils/constants/tags";
import { errorAlert } from "../utils/messages";
import { createItem, ItemRequestStatus, updateItem } from "../../api/generics";
import Company from "../../models/company";
import AnimatedCheckbox from "../checkbox/AnimatedCheckbox";

interface CompanyFormProps {
  company?: Company;
  onCancel: () => void | Promise<void>;
  onSuccess: () => void | Promise<void>;
  clearOnSuccess?: boolean;
}

const CompanyForm: React.FC<CompanyFormProps> = ({
  company,
  onCancel,
  onSuccess,
  clearOnSuccess,
}) => {
  // TODO: verify error with regular clearOnSuccess bool
  // eslint-disable-next-line
  const [shouldClearOnSuccess, _] = useState(clearOnSuccess);
  const [loading, setLoading] = useState(false);
  const [disabled, setDisabled] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [errors, setErrors] = useState<Errors>({});

  const companyRef = useRef<Company | null>(
    company !== undefined ? company : null
  );

  const updateCompanyRef = (company: Company) => {
    companyRef.current = company;
  };

  const onRucChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (companyRef.current === null) {
      return;
    }

    companyRef.current.ruc = e.target.value;
  };

  const onLegalNameChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (companyRef.current === null) {
      return;
    }

    companyRef.current.legalName = e.target.value;
  };

  const onFantasyNameChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (companyRef.current === null) {
      return;
    }

    companyRef.current.fantasyName = e.target.value;
  };

  const onPhoneNumberChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (companyRef.current === null) {
      return;
    }

    companyRef.current.phone = e.target.value;
  };

  const onPhoneNumberSecondaryChange = (
    e: React.ChangeEvent<HTMLInputElement>
  ) => {
    if (companyRef.current === null) {
      return;
    }

    companyRef.current.phoneSecondary = e.target.value;
  };

  const onIsActiveChange = (newIsActive: boolean) => {
    if (companyRef.current === null) {
      return;
    }

    companyRef.current.isActive = newIsActive;
  };

  const loadCompanyData = async (newCompany: Company) => {
    setLoading(true);
    setLoading(false);
  };

  const onClose = () => {
    clearForm();
    onCancel();
  };

  const clearForm = () => {
    updateCompanyRef({});
  };

  useEffect(() => {
    if (company !== undefined) {
      loadCompanyData(company);
    } else {
      updateCompanyRef({});
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [company]);

  if (loading) {
    return <Loading />;
  }

  const onSaveClick = async () => {
    if (companyRef.current === null) {
      return;
    }

    const company = companyRef.current;

    let requestPromise: Promise<ItemRequestStatus<Company>>;

    if (company.id === undefined) {
      requestPromise = createItem("/admin/companies/create/", company);
    } else {
      requestPromise = updateItem(`/admin/companies/${company.id}/`, company);
    }

    const requestStatus = await requestPromise;

    if (requestStatus.status !== SUCCESS) {
      let message = "Ha ocurrido un error!!";
      if (requestStatus.detail !== undefined) {
        message = requestStatus.detail;
      }
      errorAlert(message);
    } else {
      if (shouldClearOnSuccess) {
        setErrors({});
        clearForm();
      }

      onSuccess();
    }

    setErrors(requestStatus.errors!);

    setSubmitting(false);
    setDisabled(false);
  };

  return (
    <>
      <CFormGroup>
        <CRow>
          <CCol md={2}>
            <CLabel>
              <span className="text-danger">*</span> RUC
            </CLabel>
          </CCol>
          <CCol>
            <CInput
              type="text"
              placeholder="Ej: 123456-7"
              disabled={disabled}
              defaultValue={companyRef.current ? companyRef.current.ruc : ""}
              onChange={onRucChange}
            ></CInput>
            <FieldErrors
              errors={getFieldErrors("ruc", errors) as string[]}
            ></FieldErrors>
          </CCol>
        </CRow>
      </CFormGroup>
      <CFormGroup>
        <CRow>
          <CCol md={2}>
            <CLabel>
              <span className="text-danger">*</span> Nombre Social
            </CLabel>
          </CCol>
          <CCol>
            <CInput
              type="text"
              placeholder="Ej: Mi Compañía SRL"
              disabled={disabled}
              onChange={onLegalNameChange}
              defaultValue={
                companyRef.current ? companyRef.current.legalName : ""
              }
            ></CInput>
            <FieldErrors
              errors={getFieldErrors("legalName", errors) as string[]}
            ></FieldErrors>
          </CCol>
        </CRow>
      </CFormGroup>
      <CFormGroup>
        <CRow>
          <CCol md={2}>
            <CLabel>
              <span className="text-danger">*</span> Nombre de Fantasía
            </CLabel>
          </CCol>
          <CCol>
            <CInput
              type="text"
              placeholder="Ej: Mi Compañía"
              disabled={disabled}
              onChange={onFantasyNameChange}
              defaultValue={
                companyRef.current ? companyRef.current.fantasyName : ""
              }
            ></CInput>
            <FieldErrors
              errors={getFieldErrors("fantasyName", errors) as string[]}
            ></FieldErrors>
          </CCol>
        </CRow>
      </CFormGroup>
      <CFormGroup>
        <CRow>
          <CCol md={2}>
            <CLabel>
              <span className="text-danger">*</span> Activo:
            </CLabel>
          </CCol>
          <CCol>
            <CRow>
              <AnimatedCheckbox
                defaultValue={
                  companyRef.current ? companyRef.current.isActive : true
                }
                onChange={onIsActiveChange}
              ></AnimatedCheckbox>
            </CRow>

            <FieldErrors
              errors={getFieldErrors("uploadDocuments", errors) as string[]}
            ></FieldErrors>
          </CCol>
        </CRow>
      </CFormGroup>

      <CFormGroup>
        <CRow>
          <CCol md={2}>
            <CLabel>
              <span className="text-danger">*</span> Teléfono
            </CLabel>
          </CCol>
          <CCol>
            <CInput
              type="text"
              placeholder="Ej: (0961) 700 511"
              disabled={disabled}
              onChange={onPhoneNumberChange}
              defaultValue={companyRef.current ? companyRef.current.phone : ""}
            ></CInput>
            <FieldErrors
              errors={getFieldErrors("phone", errors) as string[]}
            ></FieldErrors>
          </CCol>
        </CRow>
      </CFormGroup>

      <CFormGroup>
        <CRow>
          <CCol md={2}>
            <CLabel>Teléfono Secundario</CLabel>
          </CCol>
          <CCol>
            <CInput
              type="text"
              placeholder="Ej: (0961) 700 511"
              disabled={disabled}
              onChange={onPhoneNumberSecondaryChange}
              defaultValue={
                companyRef.current ? companyRef.current.phoneSecondary : ""
              }
            ></CInput>
            <FieldErrors
              errors={getFieldErrors("phoneSecondary", errors) as string[]}
            ></FieldErrors>
          </CCol>
        </CRow>
      </CFormGroup>

      <CFormGroup className="float-right">
        <CButtonGroup>
          <CButton type="button" color="secondary" onClick={onClose}>
            Salir
          </CButton>
          <CButton
            className="btn btn-primary"
            disabled={disabled}
            onClick={onSaveClick}
          >
            {submitting ? (
              <Spinner
                animation="grow"
                style={{
                  height: "17px",
                  width: "17px",
                  marginTop: "auto",
                  marginBottom: "auto",
                  marginRight: "10px",
                }}
              />
            ) : (
              <></>
            )}
            {submitting ? "Guardando..." : "Guardar"}
          </CButton>
        </CButtonGroup>
      </CFormGroup>
    </>
  );
};

export default CompanyForm;
